import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

const DEFAULT_BLOCK_DURATION = 5000;
const DISMISS_REBLOCK_GUARD_MS = 400;
const BLUR_BLOCK_CHECK_DELAY_MS = 80;
const DEVTOOLS_CHECK_INTERVAL_MS = 500;
const DEVTOOLS_SIZE_THRESHOLD = 160;
const TAB_DUPLICATION_CHANNEL = "mileslide-tab-duplication";

const isMacPlatform = () => {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPod|iPhone|iPad/i.test(navigator.platform);
};

const isScreenshotShortcut = (event) => {
  const key = (event.key || "").toLowerCase();
  const code = event.code || "";
  const mac = isMacPlatform();

  if (key === "printscreen" || code === "PrintScreen") return true;

  // Mac: Cmd+Shift+3/4/5 (screenshot/recording shortcuts)
  if (mac && event.metaKey && event.shiftKey) {
    // Check by code
    if (["Digit3", "Digit4", "Digit5"].includes(code)) {
      return true;
    }
    // Check by key (handles event.key being "3", "4", "5")
    if (["3", "4", "5"].includes(key)) {
      return true;
    }
  }

  if (!mac && event.altKey && (key === "printscreen" || code === "PrintScreen")) {
    return true;
  }

  if (!mac && event.metaKey && event.shiftKey && key === "s") {
    return true;
  }

  if (!mac && event.ctrlKey && event.shiftKey && ["s", "4"].includes(key)) {
    return true;
  }

  return false;
};

const isPrintShortcut = (event) => {
  const key = (event.key || "").toLowerCase();
  return (event.ctrlKey || event.metaKey) && key === "p";
};

const isInFullscreenMode = () => {
  if (typeof document === "undefined") return false;

  return Boolean(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement ||
    document.webkitIsFullScreen
  );
};

const isBlockedShortcut = (event) =>
  isScreenshotShortcut(event) || isPrintShortcut(event);

const makeTabId = () =>
  `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const useScreenshotProtection = ({
  blockDuration = DEFAULT_BLOCK_DURATION,
  onAttempt,
  suppressFocusLossBlocking = false,
  enableDevToolsDetection = true,
  enableScreenRecordingDetection = true,
  enableIframeCaptureProtection = true,
  enableTabDuplicationDetection = true,
  enableClipboardDetection = true
} = {}) => {

  const [isBlocked, setIsBlocked] = useState(false);

  const unblockTimerRef = useRef(null);
  const blurTimerRef = useRef(null);
  const ignoreFocusLossUntilRef = useRef(0);
  const devtoolsSuspicionCountRef = useRef(0);
  const devtoolsCooldownUntilRef = useRef(0);
  const tabIdRef = useRef("");

  const clearUnblockTimer = useCallback(() => {
    if (unblockTimerRef.current) {
      clearTimeout(unblockTimerRef.current);
      unblockTimerRef.current = null;
    }
  }, []);

  const clearBlurTimer = useCallback(() => {
    if (blurTimerRef.current) {
      clearTimeout(blurTimerRef.current);
      blurTimerRef.current = null;
    }
  }, []);

  const blockNow = useCallback((reason = "unknown") => {

    flushSync(() => setIsBlocked(true));

    if (typeof onAttempt === "function") {
      onAttempt(reason);
    }

    clearUnblockTimer();

    unblockTimerRef.current = setTimeout(() => {
      setIsBlocked(false);
      unblockTimerRef.current = null;
    }, blockDuration);

  }, [blockDuration, clearUnblockTimer, onAttempt]);

  const dismissBlock = useCallback(() => {

    ignoreFocusLossUntilRef.current =
      Date.now() + DISMISS_REBLOCK_GUARD_MS;

    clearUnblockTimer();

    flushSync(() => setIsBlocked(false));

  }, [clearUnblockTimer]);

  const shouldIgnoreFocusLoss = useCallback(
    () => Date.now() < ignoreFocusLossUntilRef.current,
    []
  );

  /* ---------------------------
     Keyboard Detection
  --------------------------- */

  useEffect(() => {

    const onKeyAction = (event) => {
      if (event.defaultPrevented) return;
      
      // For screenshot shortcuts, check even if repeat is true (important for Mac)
      if (!isBlockedShortcut(event)) return;

      event.preventDefault();
      blockNow("screenshot");
    };

    const onBeforePrint = () => blockNow("print");

    window.addEventListener("keydown", onKeyAction, true);
    window.addEventListener("keyup", onKeyAction, true);
    window.addEventListener("beforeprint", onBeforePrint, true);

    return () => {
      window.removeEventListener("keydown", onKeyAction, true);
      window.removeEventListener("keyup", onKeyAction, true);
      window.removeEventListener("beforeprint", onBeforePrint, true);
    };

  }, [blockNow]);

  /* ---------------------------
     Focus Loss Detection
  --------------------------- */

  useEffect(() => {

    const blockOnFocusLoss = () => {

      if (
        suppressFocusLossBlocking ||
        shouldIgnoreFocusLoss() ||
        isInFullscreenMode()
      ) return;

      blockNow("focus-loss");
    };

    const onVisibilityChange = () => {
      if (document.hidden) blockOnFocusLoss();
    };

    const onWindowBlur = () => {

      if (
        suppressFocusLossBlocking ||
        shouldIgnoreFocusLoss() ||
        isInFullscreenMode()
      ) return;

      // Block immediately on blur to reduce the capture window when users switch away.
      blockNow("focus-loss");

      clearBlurTimer();

      blurTimerRef.current = setTimeout(() => {

        blurTimerRef.current = null;

        // If focus quickly returns (modal/tooling focus flicker), dismiss the block.
        if (document.hasFocus()) {
          dismissBlock();
        }

      }, BLUR_BLOCK_CHECK_DELAY_MS);

    };

    const onWindowFocus = () => {
      if (!blurTimerRef.current) return;

      clearBlurTimer();

      if (document.hasFocus()) {
        dismissBlock();
      }
    };

    window.addEventListener("blur", onWindowBlur, true);
    window.addEventListener("focus", onWindowFocus, true);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("blur", onWindowBlur, true);
      window.removeEventListener("focus", onWindowFocus, true);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      clearBlurTimer();
    };

  }, [blockNow, clearBlurTimer, dismissBlock, shouldIgnoreFocusLoss, suppressFocusLossBlocking]);

  /* ---------------------------
     DevTools Detection
  --------------------------- */

  useEffect(() => {

    if (!enableDevToolsDetection) return;

    const detectDevTools = () => {

      if (isInFullscreenMode()) {
        devtoolsSuspicionCountRef.current = 0;
        return;
      }

      const widthGap = Math.abs(window.outerWidth - window.innerWidth);
      const heightGap = Math.abs(window.outerHeight - window.innerHeight);

      const looksOpen =
        widthGap > DEVTOOLS_SIZE_THRESHOLD ||
        heightGap > DEVTOOLS_SIZE_THRESHOLD;

      if (!looksOpen) {
        devtoolsSuspicionCountRef.current = 0;
        return;
      }

      devtoolsSuspicionCountRef.current++;

      if (Date.now() < devtoolsCooldownUntilRef.current) return;

      if (devtoolsSuspicionCountRef.current >= 2) {

        devtoolsCooldownUntilRef.current = Date.now() + blockDuration;
        devtoolsSuspicionCountRef.current = 0;

        blockNow("devtools");
      }
    };

    const timer = setInterval(
      detectDevTools,
      DEVTOOLS_CHECK_INTERVAL_MS
    );

    return () => clearInterval(timer);

  }, [blockDuration, blockNow, enableDevToolsDetection]);

  /* ---------------------------
     Screen Recording Detection
  --------------------------- */

  useEffect(() => {

    if (!enableScreenRecordingDetection) return;

    if (
      !navigator?.mediaDevices ||
      typeof navigator.mediaDevices.getDisplayMedia !== "function"
    ) return;

    const mediaDevices = navigator.mediaDevices;

    const descriptor = Object.getOwnPropertyDescriptor(
      mediaDevices,
      "getDisplayMedia"
    );

    if (!descriptor?.writable) return;

    const original = mediaDevices.getDisplayMedia.bind(mediaDevices);

    mediaDevices.getDisplayMedia = async (...args) => {
      blockNow("screen-recording");
      return original(...args);
    };

    return () => {
      mediaDevices.getDisplayMedia = original;
    };

  }, [blockNow, enableScreenRecordingDetection]);

  /* ---------------------------
     Clipboard Screenshot Detection
  --------------------------- */

  useEffect(() => {

    if (!enableClipboardDetection) return;

    const onPaste = (event) => {

      const items = event.clipboardData?.items || [];

      for (const item of items) {

        if (item.type.includes("image")) {
          blockNow("clipboard-image");
          break;
        }
      }
    };

    document.addEventListener("paste", onPaste);

    return () => {
      document.removeEventListener("paste", onPaste);
    };

  }, [blockNow, enableClipboardDetection]);

  /* ---------------------------
     iFrame Protection
  --------------------------- */

  useEffect(() => {

    if (!enableIframeCaptureProtection) return;

    if (window.self === window.top) return;

    blockNow("iframe");

    try {
      window.top.location = window.location;
    } catch { }

  }, [blockNow, enableIframeCaptureProtection]);

  /* ---------------------------
     Tab Duplication Detection
  --------------------------- */

  useEffect(() => {

    if (!enableTabDuplicationDetection) return;
    if (typeof BroadcastChannel !== "function") return;

    if (!tabIdRef.current) {
      tabIdRef.current = makeTabId();
    }

    const tabId = tabIdRef.current;

    const channel = new BroadcastChannel(
      TAB_DUPLICATION_CHANNEL
    );

    const onMessage = (event) => {

      const payload = event?.data;

      if (!payload || payload.id === tabId) return;

      if (payload.type === "TAB_HELLO") {

        channel.postMessage({
          type: "TAB_PRESENT",
          id: tabId
        });

        return;
      }

      if (payload.type === "TAB_PRESENT") {
        blockNow("duplicate-tab");
      }
    };

    channel.addEventListener("message", onMessage);

    channel.postMessage({
      type: "TAB_HELLO",
      id: tabId
    });

    return () => {
      channel.removeEventListener("message", onMessage);
      channel.close();
    };

  }, [blockNow, enableTabDuplicationDetection]);

  return {
    isBlocked,
    triggerBlock: blockNow,
    dismissBlock
  };
};